import { ShieldX, TriangleAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { OutcomeUnknownState } from "@/components/ui/system-state";
import { toast } from "@/components/ui/toast";
import { CrossModuleLink, SpecimenCard, SpecimenSection } from "@/features/system-states/system-state-page";

const CASES = [
  {
    id: "T-A91",
    title: "Broadcast timeout",
    description:
      "The transaction was broadcast to the network but no confirmation or failure was received within the confirmation window.",
    trace: "trc_a91f3c72",
  },
  {
    id: "T-B27",
    title: "Nonce conflict",
    description:
      "A replacement transaction may have been mined with the same nonce. On-chain state cannot be reconciled automatically.",
    trace: "trc_b27d09e1",
  },
  {
    id: "T-C15",
    title: "Provider disagreement",
    description:
      "The custody and signing providers report conflicting confirmation status for the same transaction hash.",
    trace: "trc_c15a84b0",
  },
];

export default function SysOutcomeUnknown() {
  function openTicket(caseId: string) {
    toast({
      variant: "success",
      title: "Support ticket created",
      description: `Ticket SUP-7712 linked to ${caseId}. An operator will verify the on-chain outcome.`,
    });
  }

  return (
    <>
      <SpecimenSection label="Terminal danger banner">
        <InfoBanner variant="danger" title="This state is terminal and never retried" className="mb-0">
          OUTCOME_UNKNOWN means the result of a broadcast could not be determined. Retrying can double-spend. The
          transfer must be resolved by manual on-chain verification.
        </InfoBanner>
      </SpecimenSection>

      <SpecimenSection label="Danger cases">
        <div className="grid gap-4 lg:grid-cols-3">
          {CASES.map((c) => (
            <div key={c.id} className="flex flex-col gap-3 rounded-lg border border-danger-border bg-danger-soft p-5">
              <div className="flex items-center justify-between gap-3">
                <Badge variant="danger" dot={false}>
                  OUTCOME_UNKNOWN
                </Badge>
                <ShieldX aria-hidden className="size-5 shrink-0 text-danger" />
              </div>
              <div>
                <div className="text-sm font-bold text-ink">
                  {c.id} · {c.title}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-muted">{c.description}</p>
              </div>
              <div className="mt-auto flex flex-col gap-2">
                <div className="font-mono text-[11px] text-muted">Trace {c.trace}</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => openTicket(c.id)}>
                    Open support ticket
                  </Button>
                  <CrossModuleLink variant="secondary" className="min-h-[28px] px-[10px] text-xs">
                    View on explorer
                  </CrossModuleLink>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SpecimenSection>

      <SpecimenSection label="What this state is not">
        <SpecimenCard label="Disambiguation — the state is exact, not an approximation">
          <ul className="flex flex-col gap-2 text-sm text-muted">
            <li className="flex gap-2">
              <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-warning" />
              <span>
                Not <span className="font-semibold text-ink">FAILED</span> — a failure is a confirmed terminal outcome
                and may be retried.
              </span>
            </li>
            <li className="flex gap-2">
              <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-warning" />
              <span>
                Not <span className="font-semibold text-ink">PENDING</span> — pending is expected to resolve on its own;
                this state will not.
              </span>
            </li>
            <li className="flex gap-2">
              <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-warning" />
              <span>
                Not <span className="font-semibold text-ink">STALE</span> — stale affects data freshness only and never
                blocks resolution.
              </span>
            </li>
          </ul>
        </SpecimenCard>
      </SpecimenSection>

      <SpecimenSection label="Operator console detail">
        <OutcomeUnknownState
          description="transaction.broadcast.hash=0x9f…41ab — confirmation status unresolved after 12 blocks. reconciliation=MANUAL_REQUIRED, retry_allowed=false."
          traceId="trc_a91f3c72"
        />
      </SpecimenSection>
    </>
  );
}
